import { getItemUniqueKey, getPackageId } from 'checkout/lib/utils/accommodation/cart'
import { computeCabinPricing } from 'checkout/lib/utils/cruises/pricing'
import { CHECKOUT_ITEM_TYPE_CRUISE } from 'constants/checkout'
import { OFFER_TYPE_CRUISE } from 'constants/offer'
import { getCruiseDurationByItinerary } from 'lib/cruises/cruiseUtils'
import { buildCruiseSummaryBreakdown } from 'checkout/lib/utils/cruises/summary'
import { getBreakdownTitle } from 'checkout/utils'
import { isCheckoutReviewAndPaymentStepId } from 'checkout/constants/pages'
import { INTL_DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import { sum } from 'lib/array/arrayUtils'
import { formatCruiseCabinTypeLabel } from 'hooks/Cruise/useCruiseCabinTypeLabels'
import { formatSaleUnitLabel } from 'hooks/useSaleUnitFormatting'
import { getCruiseDepositAmount } from './booking'
import getLoyaltyProductType from 'loyalty/lib/getLoyaltyProductType'
import type { IntlShape } from 'react-intl'

export function isCruiseOfferView(
  view: App.Checkout.AccommodationOfferView,
): view is App.Checkout.CruiseAccommodationOfferView {
  return view.offerType === OFFER_TYPE_CRUISE
}

function dummyCruiseItemView(
  intl: IntlShape,
  item: App.Checkout.CruiseItem,
  kind: 'cruise',
  offer: App.CruiseOffer | undefined,
): App.Checkout.CruiseAccommodationItemView {
  return {
    title: '',
    kind,
    packageId: getPackageId(item),
    uniqueKey: getItemUniqueKey(item),
    item,
    primaryTitle: '',
    occupancy: { adults: 0, children: 0, infants: 0 },
    totals: {
      price: 0,
      memberPrice: 0,
      value: 0,
      surcharge: 0,
      taxesAndFees: 0,
      memberValue: 0,
      extraGuestSurcharge: 0,
    },
    depositAmount: 0,
    removeLabel: intl.formatMessage({
      id: 'OvoNo1O2QUEtiETWwRWC',
      defaultMessage: 'remove',
      description: 'Remove button label for cruise cabin item in checkout',
    }),
    loyalty: {
      productType: getLoyaltyProductType(offer),
    },
  }
}

export function buildCruiseItemView(
  intl: IntlShape,
  item: App.Checkout.CruiseItem,
  offer: App.CruiseOffer,
  index: number,
  cabinSelectionData: App.Cruises.BookingCabinSelectionData,
  cabinPricingData: App.Cruises.BookingCabinPricingData,
  rateListData: App.Cruises.CruiseBookingRateListData,
  rateDetailsData: App.Cruises.BookingRateDetailsData,
  isReviewAndPaymentStep: boolean,
  currentBookingStepLowestPrice: number,
  consolidatedPaymentSchedule?: App.Cruises.CruiseConsolidatedPaymentSchedule,
): App.WithDataStatus<App.Checkout.CruiseAccommodationItemView> {
  if (!offer) {
    return {
      hasRequiredData: false,
      data: dummyCruiseItemView(intl, item, CHECKOUT_ITEM_TYPE_CRUISE, offer),
    }
  }

  const { cabinSelection } = cabinSelectionData

  const rateDetails = rateDetailsData.detailsByRate[item.componentId || ''] as App.CruiseCabinRateDetailsResponse
  const cabinRate = rateListData.rates.find(rate => rate.componentId === item.componentId)

  const primaryTitle = cabinRate?.pricedCategoryCode || ''
  const cabinCategory = offer.ship.cabinCategories?.find(category => category.code === item.cabinCode)

  const departures = offer.departures ? Object.values(offer.departures).flat() : []
  const departure = departures.find(departure => departure.id === item.departureId)

  const {
    taxesAndFees,
    price,
    memberPrice,
    value: valuedUpTotal,
    memberValue: valuedUpMemberTotal,
  } = computeCabinPricing(cabinSelectionData, cabinPricingData, cabinRate?.priceDetails)
  const depositAmount = getCruiseDepositAmount(item.bookingId, consolidatedPaymentSchedule)

  const hasRequiredData = isReviewAndPaymentStep ? !!cabinSelection : !!offer
  const saleUnitLabel = formatSaleUnitLabel(intl, offer.saleUnit) ?? offer.saleUnit
  const cabinTypeLabel = item.cabinType ? formatCruiseCabinTypeLabel(intl, item.cabinType) : ''
  const titleLabel = [`${saleUnitLabel} ${index + 1}`, cabinTypeLabel].filter(Boolean).join(' - ')

  return {
    hasRequiredData,
    data: {
      title: [titleLabel, offer.name].filter(Boolean).join(': '),
      loyalty: {
        productType: getLoyaltyProductType(offer),
      },
      item: { ...item, offerName: offer.name },
      kind: CHECKOUT_ITEM_TYPE_CRUISE,
      packageId: item.departureId,
      uniqueKey: item.departureId,
      departure,
      occupancy: item.occupancy,
      totals: {
        surcharge: 0,
        memberPrice,
        price,
        taxesAndFees,
        value: valuedUpTotal,
        memberValue: valuedUpMemberTotal,
        extraGuestSurcharge: 0,
      },
      currentBookingStepLowestPrice,
      luxPlusInclusionsByTier: item.luxPlusInclusionsByTier,
      depositAmount,
      primaryTitle,
      ...(cabinRate && { cabinRate }),
      ...(cabinCategory && { cabinCategory }),
      ...(rateDetails && { rateDetails }),
      ...(cabinSelection && { cabinSelection }),
      ...(cabinPricingData && { cabinPricingData }),

      removeLabel: intl.formatMessage({
        id: 'OvoNo1O2QUEtiETWwRWC',
        defaultMessage: 'remove',
        description: 'Remove button label for cruise cabin item in checkout',
      }),
    },
  }
}

export function getCruiseBreakdownView(
  intl: IntlShape,
  offerView: App.Checkout.CruiseAccommodationOfferView,
  hidePrice: boolean,
  isSpoofed = false,
): App.Checkout.PriceBreakdownView {
  const { startDate, endDate, saleUnit, itemViews, mainLabel, currentCheckoutPage } = offerView

  const items = itemViews.map((itemView, index) => {
    return getCruiseBreakdownItemView(intl, itemView, index, saleUnit, hidePrice, currentCheckoutPage, offerView.offer, isSpoofed)
  })

  const cruiseDuration = getCruiseDurationByItinerary(offerView.offer?.itinerary ?? [])
  const formattedStartDate = startDate ? intlDateFormatter(startDate, INTL_DMY_CASUAL_FORMAT, intl.locale) : intl.formatMessage({
    id: 'F119Vszr42vnYGDKQTxL',
    defaultMessage: 'start',
    description: 'Fallback label for cruise start date when date is unavailable',
  })
  const formattedEndDate = endDate ? intlDateFormatter(endDate, INTL_DMY_CASUAL_FORMAT, intl.locale) : intl.formatMessage({
    id: 'tglXhXtQ4gf0BcQ_gjov',
    defaultMessage: 'end',
    description: 'Fallback label for cruise end date when date is unavailable',
  })
  const price = sum(items, item => (item.price ?? 0))
  const memberPrice = sum(items, item => (item.memberPrice ?? 0))
  const cabins = items.length
  const passengers = itemViews.reduce(
    (acc, { occupancy }) => acc + occupancy.adults + (occupancy.children || 0) + (occupancy.infants || 0),
    0,
  )
  const cabinsLabel = intl.formatMessage(
    {
      id: 'z7qTI_boWbOzWDj_tCkD',
      defaultMessage: '{count, plural, one {# cabin} other {# cabins}}',
      description: 'Cruise breakdown cabin count label in the cruise offer or booking section',
    },
    { count: cabins },
  )
  const passengersLabel = intl.formatMessage(
    {
      id: '6ZSOTxAf1QxWxgb0cY7t',
      defaultMessage: '{count, plural, one {# passenger} other {# passengers}}',
      description: 'Cruise breakdown passenger count label in the cruise offer or booking section',
    },
    { count: passengers },
  )
  const cruiseNightsLabel = intl.formatMessage(
    {
      id: 'm-de6WR-0aTPTFEK5eDl',
      defaultMessage: '{count, plural, one {# night} other {# nights}}',
      description: 'Cruise breakdown night count label in the cruise offer or booking section',
    },
    { count: cruiseDuration - 1 },
  )
  const cabinsAndPassengersLabel = passengers ? intl.formatMessage(
    {
      id: 'epkv6LGs8zIzw5noT2kr',
      defaultMessage: '{cabinsLabel} · {passengersLabel}',
      description: 'Cruise breakdown summary showing cabin and passenger counts',
    },
    {
      cabinsLabel,
      passengersLabel,
    },
  ) : ''

  return {
    title: getBreakdownTitle(offerView),
    description: mainLabel,
    additionalInfoText: [
      `${offerView.offer?.cruiseLine.name} - ${offerView.offer?.ship.name}`,
      intl.formatMessage(
        {
          id: 'SgVfpB9jTVywxvpzsnkJ',
          defaultMessage: '{formattedStartDate} - {formattedEndDate} - {cruiseNightsLabel}',
          description: 'Cruise breakdown date range and nights label in the cruise section',
        },
        {
          formattedStartDate,
          formattedEndDate,
          cruiseNightsLabel,
        },
      ),
      cabinsAndPassengersLabel,
    ],
    items,
    price,
    memberPrice,
    onboardCredits: items.find(item => item.onboardCredits)?.onboardCredits,
    productType: offerView.offerType,
    itemsAlwaysVisible: false,
  }
}

function getCruiseBreakdownItemView(
  intl: IntlShape,
  itemView: App.Checkout.CruiseAccommodationItemView,
  index: number,
  saleUnit: string,
  hidePrice: boolean,
  currentCheckoutPage: string,
  offer?: App.CruiseOffer,
  isSpoofed = false,
): App.Checkout.CruiseItemBreakdownView {
  const {
    additionalElements,
    additionalInfo,
  } = buildCruiseSummaryBreakdown(intl, itemView, isSpoofed)
  const cancellationPolicy = buildItemViewCancellationPolicy(itemView)
  const onboardCredits = buildItemViewOnboardCredits(itemView)
  const saleUnitLabel = formatSaleUnitLabel(intl, saleUnit) ?? saleUnit
  const cabinTypeLabel = itemView.item.cabinType ? formatCruiseCabinTypeLabel(intl, itemView.item.cabinType) : ''

  const showPrice = isCheckoutReviewAndPaymentStepId(currentCheckoutPage) && !hidePrice
  const price = showPrice ? (itemView?.totals.price ?? 0) - itemView.totals.taxesAndFees : undefined
  const memberPrice = showPrice ? (itemView?.totals.memberPrice ?? 0) - itemView.totals.taxesAndFees : undefined
  const title = [`${saleUnitLabel} ${index + 1}`, cabinTypeLabel].filter(Boolean).join(' - ')

  return {
    title,
    additionalInfo,
    cancellationPolicy,
    additionalElements,
    fareOptionName: itemView.cabinRate?.name,
    deckLocation: buildCruiseSummaryDeckLocation(itemView, offer),
    price,
    memberPrice,
    taxesAndFees: itemView.totals.taxesAndFees,
    offerId: itemView.item.offerId,
    itemId: itemView.item.itemId,
    luxPlusInclusionsByTier: itemView.luxPlusInclusionsByTier,
    itemType: 'cruise',
    ...(onboardCredits && { onboardCredits }),
  }
}

function buildCruiseSummaryDeckLocation(
  itemView: App.Checkout.CruiseAccommodationItemView,
  offer?: App.CruiseOffer,
): App.Checkout.CruiseItemBreakdownView['deckLocation'] {
  const { cabinNumber, deckId } = itemView.item

  if (!cabinNumber || !deckId || !offer) {
    return undefined
  }

  const deck = offer.ship.decks?.find((deck) => (
    deck.id === deckId ||
    String(deck.externalId) === deckId
  ))

  if (!deck?.imageId) {
    return undefined
  }

  return {
    cabinNumber,
    deckId,
    deckName: deck.name,
    imageId: deck.imageId,
    shipId: offer.ship.id,
    selectedCabinPosition: deck.shipCabinPosition?.find(position => position.cabinNumber === cabinNumber),
  }
}

function buildItemViewOnboardCredits(
  itemView: App.Checkout.CruiseAccommodationItemView,
): App.CruiseOnboardCredits | undefined {
  const { cabinSelection } = itemView
  const onboardCredits = cabinSelection?.pricing?.onboardCredit
  const onboardCreditAmount = onboardCredits?.amount
  const onboardCreditCurrencyCode = onboardCredits?.currency

  if (onboardCreditAmount && onboardCreditCurrencyCode) {
    return {
      amount: onboardCreditAmount,
      currencyCode: onboardCreditCurrencyCode,
    }
  }
}

function buildItemViewCancellationPolicy(
  itemView: App.Checkout.CruiseAccommodationItemView,
): App.Cruises.CruiseCancellationPolicyView | undefined {
  const pricing = itemView.cabinSelection?.pricing
  return {
    isNonRefundable: pricing?.isNonRefundable,
    cancellationSchedule: pricing?.cancellationSchedule,
    cancellationPolicy: pricing?.cancellationPolicy,
  }
}
